import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
    Users, 
    CheckCircle, 
    Clock, 
    AlertCircle, 
    TrendingUp, 
    BarChart3,
    Activity,
    ArrowUpRight,
    ArrowDownRight
} from 'lucide-react';
import axios from 'axios';

const API_BASE = (import.meta.env.VITE_APP_API_URL && import.meta.env.VITE_APP_API_URL !== "undefined") ? import.meta.env.VITE_APP_API_URL : "https://apiessentialcarehomehealth.saasbizhub.com/api/v1";

const StatCard = ({ title, value, icon: Icon, color, trend, trendValue }) => (
    <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
    >
        <div className="flex justify-between items-start mb-4">
            <div className={`p-3 rounded-xl ${color} bg-opacity-10`}>
                <Icon className={`w-6 h-6 ${color.replace('bg-', 'text-')}`} />
            </div>
            {trend && (
                <div className={`flex items-center text-sm font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                    {trend === 'up' ? <ArrowUpRight className="w-4 h-4 mr-1" /> : <ArrowDownRight className="w-4 h-4 mr-1" />}
                    {trendValue}
                </div>
            )}
        </div>
        <h3 className="text-gray-500 text-sm font-medium mb-1">{title}</h3>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
    </motion.div>
);

export default function CrmDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchStats = async () => {
            const token = localStorage.getItem('admin_token');
            if (!token) return;

            try {
                setLoading(true);
                const response = await axios.get(`${API_BASE}/admin/crm/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setStats(response.data);
            } catch (err) {
                setError('Failed to load dashboard stats');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (loading) return (
        <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
    );

    if (error) return (
        <div className="p-8 text-center text-red-600 bg-red-50 rounded-xl border border-red-100">
            <AlertCircle className="w-12 h-12 mx-auto mb-4" />
            <p>{error}</p>
        </div>
    );

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8">
            <header>
                <h1 className="text-3xl font-bold text-gray-900 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                    CRM Overview
                </h1>
                <p className="text-gray-500 mt-2">Manage your leads and track performance.</p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                    title="Total Leads" 
                    value={stats?.leads_count?.total || 0} 
                    icon={Users} 
                    color="bg-blue-600"
                />
                <StatCard 
                    title="New Leads" 
                    value={stats?.leads_count?.new || 0} 
                    icon={Activity} 
                    color="bg-teal-600"
                />
                <StatCard 
                    title="Pending Tasks" 
                    value={stats?.tasks_count?.pending || 0} 
                    icon={Clock} 
                    color="bg-amber-600"
                />
                <StatCard 
                    title="Completed Today" 
                    value={stats?.tasks_count?.completed_today || 0} 
                    icon={CheckCircle} 
                    color="bg-green-600"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Status Breakdown */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <BarChart3 className="w-5 h-5 mr-2 text-teal-600" />
                            Lead Status Breakdown
                        </h3>
                    </div>
                    <div className="space-y-4">
                        {['New', 'Contacted', 'Qualified', 'Pending', 'Converted', 'Lost'].map(status => {
                            const count = stats?.leads_count?.[status.toLowerCase()] || 0;
                            const total = stats?.leads_count?.total || 1;
                            const percentage = Math.round((count / total) * 100);
                            
                            return (
                                <div key={status} className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="font-medium text-gray-600">{status}</span>
                                        <span className="text-gray-500">{count} ({percentage}%)</span>
                                    </div>
                                    <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                                        <motion.div 
                                            initial={{ width: 0 }}
                                            animate={{ width: `${percentage}%` }}
                                            className={`h-full ${
                                                status === 'Converted' ? 'bg-green-500' : 
                                                status === 'Lost' ? 'bg-red-500' : 
                                                status === 'New' ? 'bg-blue-500' : 'bg-teal-500'
                                            }`}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Service Request Breakdown */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <TrendingUp className="w-5 h-5 mr-2 text-teal-600" />
                            Service Requests
                        </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        {stats?.leads_by_service?.map(item => (
                            <div key={item.service_requested} className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                                <p className="text-sm text-gray-500 mb-1">{item.service_requested}</p>
                                <p className="text-2xl font-bold text-gray-900">{item.count}</p>
                            </div>
                        ))}
                        {(!stats?.leads_by_service || stats.leads_by_service.length === 0) && (
                            <div className="col-span-2 text-center text-gray-400 py-8">
                                No service data available
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
